import { ReactElement, useEffect, useRef } from "react";
import {
  CredentialResponseType,
  LoginErrorCode,
  LoginMethod,
} from "../../types";
import { useGetAccessToken } from "../../utils/token";
import { useSetSetting } from "../../utils/settings";
import { useAtom } from "jotai";
import { accessTokenInfoAtom } from "../../store";
import { Box } from "@chakra-ui/react";

type GoogleLoginButtonProps = {
  onInvalidUserInfo: (errorCode: LoginErrorCode) => void;
};

export function GoogleLoginButton({
  onInvalidUserInfo,
}: Readonly<GoogleLoginButtonProps>): ReactElement | null {
  const buttonRef = useRef<HTMLDivElement>(null);
  const gClientId = import.meta.env.VITE_APP_GCLOUD_CLIENT_ID;

  const getAccessToken = useGetAccessToken();
  const [, setAccessTokenInfo] = useAtom(accessTokenInfoAtom);
  const setSettingInfo = useSetSetting();

  useEffect(() => {
    const handleCredentialResponse = async (
      credentialResponse: CredentialResponseType,
    ): Promise<void> => {
      const sendData = {
        credential: credentialResponse.credential,
      };
      const token = await getAccessToken(
        LoginMethod.GoogleAuth,
        onInvalidUserInfo,
        sendData,
      );
      if (!token) return;
      setAccessTokenInfo(token);
      await setSettingInfo(token);
    };

    if (buttonRef.current && window.google) {
      window.google.accounts.id.initialize({
        client_id: gClientId,
        callback: handleCredentialResponse,
        ux_mode: "popup",
        context: "signin",
      });
      window.google.accounts.id.renderButton(buttonRef.current, {
        text: "signin_with",
        type: "standard",
        size: "large",
        theme: "outline",
        logo_alignment: "left",
      });
    }
  }, [
    gClientId,
    getAccessToken,
    onInvalidUserInfo,
    setAccessTokenInfo,
    setSettingInfo,
  ]);

  if (!gClientId) {
    return null;
  }
  return (
    <Box minH="100px">
      <div ref={buttonRef} />
    </Box>
  );
}
